import { isEmpty, isNil } from 'lodash';
import { buildCardField } from '@shared/data/cards/buildCardField';
import { Field } from '@shared/data/entity/fields/Field';
import { RelatedLookupField } from '@shared/data/entity/fields/RelatedLookupField';
import { FieldVariant } from '@shared/scenes/cards/collectionView/components/fields/FieldVariant';
import { getFieldText } from '@shared/scenes/cards/collectionView/components/fields/util/getFieldText';
import { applyComponentOverrides } from '@shared/scenes/entity/util/useViewComponents';
import {
  isInputButtonComponent,
  isInputComponent,
  isManagingInputComponent,
  isRelatedCardComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { getDraftRowFields } from './subtableDrafts';
import { SubtableRowRefs } from './useSubtableRows';

const EMPTY_REFS: SubtableRowRefs = {
  workflowTemplateRefs: null,
  personRefs: null,
  workflowEntityRefs: null,
  recurringEntitiesScheduleRefs: null,
};

/**
 * The subtable's visible columns in builder order: its input components, date ranges and input
 * buttons, minus the server-managed pointer columns and anything hidden. A date range is one
 * column, so the start and end dates it manages aren't columns of their own. The table tracks the
 * Update view template (the one the row form edits with): a field it hides is hidden as a column,
 * and its other overrides (a label) carry over. Without one, the template's own components.
 */
export function getSubtableColumns(
  workflowTemplate: Pick<API.WorkflowTemplate, 'components'> | undefined,
  updateViewTemplate?: API.ViewTemplateFragment,
): API.AnyComponent[] {
  const components = applyComponentOverrides(
    workflowTemplate?.components ?? [],
    updateViewTemplate,
  );
  return components.filter(
    component =>
      isColumnComponent(component) &&
      !isManagedByAnotherComponent(component) &&
      !('isServerManaged' in component && component.isServerManaged) &&
      !('isHidden' in component && component.isHidden),
  );
}

function isColumnComponent(component: API.AnyComponent): boolean {
  return (
    isInputComponent(component) ||
    isManagingInputComponent(component) ||
    isInputButtonComponent(component)
  );
}

/** A date range's start or end date. A related card prefilled from another stays a column. */
function isManagedByAnotherComponent(component: API.AnyComponent): boolean {
  return !isNil(component.managedByComponentId) && !isRelatedCardComponent(component);
}

export function getSubtableCellField(
  row: API.WorkflowEntityFragment,
  column: API.AnyComponent,
  refs: SubtableRowRefs | undefined,
): Field | null {
  const draftFields = getDraftRowFields(row);
  if (!isNil(draftFields)) {
    return draftFields[column.id] ?? null;
  }
  return buildCardField({ entity: row, component: column, refs: refs ?? EMPTY_REFS });
}

/**
 * A row's ephemeral number: its 1-based position in the loaded rows (sorted oldest first, and
 * paged by appending, so it holds while more load). Nothing stores it, so rows renumber when one is
 * added or deleted.
 */
export function getSubtableRowNumber(index: number): number {
  return index + 1;
}

/** A column's value in a row's slot: the field, if any, and the column it's from. */
export interface SubtableSlot {
  column: API.AnyComponent;
  field: Field | null;
}

/**
 * The three slots a row collapses to on mobile: the first column titles the row, the middle
 * columns join into a secondary line, and the last column is the trailing value. Each slot keeps
 * its column and field, so a tag column can render as a tag; the secondary line holds only those
 * with a value.
 */
export function getSubtableRowSlots(
  row: API.WorkflowEntityFragment,
  columns: API.AnyComponent[],
  refs: SubtableRowRefs | undefined,
): { title: SubtableSlot | null; secondary: SubtableSlot[]; trailing: SubtableSlot | null } {
  const slots = columns.map(column => ({
    column,
    field: getSubtableCellField(row, column, refs),
  }));
  const title = slots.shift() ?? null;
  const trailing = slots.pop() ?? null;
  const secondary = slots.filter(slot => !isEmpty(getFieldText(slot.field)));
  return { title, secondary, trailing };
}

/**
 * The variant each slot asks a column's field for, by component type: the title shows a
 * compact stack, pill or one-line value for the types that have one and the default otherwise, the subtitle
 * flows every value inline, and the trailing slot shows each compact.
 */
const TITLE_VARIANTS: Partial<Record<API.ComponentType, FieldVariant>> = {
  TAG: 'compact',
  PERSON: 'compact',
  FILE: 'compact',
  TODO: 'compact',
  GEOLOCATION: 'compact',
  SIGNATURE: 'compact',
  DATE: 'compact',
  NUMBER: 'compact',
  TEXT: 'compact',
  RELATED_CARD_LOOKUP: 'compact',
  TIME_TRACKER: 'compact',
  RELATED_CARD: 'compact',
  // Plain text in the title, truncating at its end: an address as one string, "123 Main St,
  // Springfield, IL"; the linked email or URL are the trailing slot's.
  ADDRESS: 'inline',
  EMAIL: 'inline',
  URL: 'inline',
};

export function getSubtableSlotVariant(
  slot: 'title' | 'subtitle' | 'trailing',
  { column, field }: SubtableSlot,
): FieldVariant {
  switch (slot) {
    case 'title':
      return TITLE_VARIANTS[getTitleType(column, field)] ?? 'default';
    case 'subtitle':
      return 'inline';
    case 'trailing':
      return 'compact';
  }
}

/**
 * The type the title picks its variant by: a lookup renders its looked-up field, so it takes that
 * field's type, and a looked-up email reads as plain text like an email column.
 */
function getTitleType(column: API.AnyComponent, field: Field | null): API.ComponentType {
  if (field instanceof RelatedLookupField && !isNil(field.leafComponent)) {
    return field.leafComponent.type;
  }
  return column.type;
}
